/**
 * Busy state for buttons - the house pattern for "working..." affordance
 * (the states standard, /standards/states).
 *
 *   g.busy(btn, true)   - disable + the working paint
 *   g.busy(btn, false)  - restore
 *
 * PAINT ONLY, never a box change (Andrei, Sep 2026 - the spinner era):
 * the old road inserted a `.spinner-border` in the icon's place and froze
 * the width; the spinner was wider than the glyph it replaced, so every
 * button with a label wrapped onto two lines for the flight. Now:
 *   - the element gets `.genus-busy` (scss/_busy.scss): a light band
 *     sweeping across the button + a slight dim, `disabled` + `aria-busy`,
 *     the content and the box untouched;
 *   - a button that HAS an icon (`<i class="ri-…">` direct child) gets the
 *     glyph swapped IN PLACE for the loader (`ri-loader-4-line`, rotating
 *     via `.genus-busy-icon`) - same element, same box, never two icons;
 *     a button without an icon shows the band alone; an `<svg>` icon is
 *     left as it is (the band carries the state).
 * A form control (input/select/textarea) only goes disabled + aria-busy.
 * `target` is a button/element or a selector. Turning busy on twice is a
 * no-op. Turning it off restores the glyph ONLY while the icon is still
 * ours (still in the button, still carrying `.genus-busy-icon`): content
 * the action rewrote meanwhile (an optimistic icon/label swap, a
 * re-rendered button) is kept as it is (BSP's report, 0.43.1). g.action
 * drives this for every control that starts async work; former's submit
 * pipeline rides it; free for app code too.
 */
const BUSY = 'genus-busy';
const ICON = 'genus-busy-icon';
const LOADER = 'ri-loader-4-line';

export function busy(target, on = true) {
	const el = typeof target === 'string' ? document.querySelector(target) : target;
	if (!el) return;
	if (on) {
		if (el._busy) return;
		el._busy = { disabled: el.disabled, icon: null, iconClass: null };
		el.disabled = true;
		el.setAttribute('aria-busy', 'true');
		if (el.matches('input, select, textarea')) return;
		el.classList.add(BUSY);
		const icon = el.querySelector(':scope > i');
		if (icon && /(^|\s)ri-[a-z0-9-]+/.test(icon.className)) {
			el._busy.icon = icon;
			el._busy.iconClass = icon.className;
			icon.className = icon.className.replace(/(^|\s)ri-[a-z0-9-]+/, '$1' + LOADER) + ' ' + ICON;
		}
	} else {
		if (!el._busy) return;
		const { icon, iconClass } = el._busy;
		// the glyph goes back only while the icon is still ours: a rewritten
		// button (or a re-set className) is left alone
		if (icon && icon.parentNode === el && icon.classList.contains(ICON)) icon.className = iconClass;
		el.classList.remove(BUSY);
		el.disabled = el._busy.disabled;
		el.removeAttribute('aria-busy');
		delete el._busy;
	}
}
